import { Chart, Copy, Edit, ImportFile, Remove, Share } from '@app/components/various/Icons';
import { getIsCsvImportEnabled, getIsLoggedIn, showModal } from '@app/ducks';
import { paths } from '@app/paths';
import { useDeleteSelections } from '@app/services/hooks/orders/useDeleteSelections';
import { Dropdown, IconComponentProps } from '@typings';
import { omit } from 'ramda';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useAmplitude } from '../../analytics/useAmplitude';
import { useAvailableOrderDetails } from '../useAvailableOrderDetails';
import { useConfirmationGuard } from '../useConfirmationGuard';
interface Props {
  onCopyToNewSelection: () => void;
}

const RemoveIcon = (props: IconComponentProps) => <Remove {...props} className="text-error-45" />;

export const useOrderOptions = ({ onCopyToNewSelection }: Props): Dropdown.MenuItem[] => {
  const { t } = useTranslation(['selections', 'confirmationConfig', 'selectionOverview']);
  const dispatch = useDispatch();
  const isUserLoggedIn = useSelector(getIsLoggedIn);
  const isCsvImportEnabled = useSelector(getIsCsvImportEnabled);

  const navigate = useNavigate();
  const confirmationGuard = useConfirmationGuard();
  const { trackEvent } = useAmplitude();
  const { orderId } = useAvailableOrderDetails();
  const { deleteSelectionByIds } = useDeleteSelections();

  const openSelectionShareModal = () => {
    dispatch(showModal('SelectionShareModal'));
    trackEvent({ name: 'bottomBar.button.share.click' });
  };

  const openEditModal = () => {
    dispatch(showModal('EditOrderModal'));
    trackEvent({ name: 'bottomBar.button.edit.click' });
  };

  const openOverviewModal = () => {
    dispatch(showModal('SelectionOverviewModal'));
    trackEvent({ name: 'bottomBar.button.overview.click' });
  };

  const openCopySelectionModal = () => {
    onCopyToNewSelection();
  };

  const openImportModal = () => {
    dispatch(showModal('SelectionImportModal'));
    trackEvent({ name: 'bottomBar.button.import.click' });
  };

  const deleteOrder = confirmationGuard(() => ({
    onOk: () => {
      deleteSelectionByIds.mutate([orderId], { onSuccess: () => navigate(paths.SELECTIONS) });
    },
    ...t('confirmationConfig:delete_order', { returnObjects: true }),
  }));

  return React.useMemo(() => {
    const options = [
      {
        icon: Edit,

        isVisible: isUserLoggedIn,
        key: 'edit',
        label: t('selections:edit_selection'),
        onClick: openEditModal,
      },
      {
        icon: Copy,

        isVisible: isUserLoggedIn,
        key: 'copy',
        label: t('selections:copy_to_new_selection'),
        onClick: openCopySelectionModal,
      },
      {
        icon: Chart,

        isVisible: true,
        key: 'overview',
        label: t('selectionOverview:selection_overview'),
        onClick: openOverviewModal,
      },
      {
        icon: Share,

        isVisible: isUserLoggedIn,
        key: 'share',
        label: t('selections:share_selection'),
        onClick: openSelectionShareModal,
      },
      {
        icon: ImportFile,
        isVisible: isCsvImportEnabled && isUserLoggedIn,
        key: 'import',
        label: t('selections:import_selection_csv'),
        onClick: openImportModal,
      },
      {
        icon: RemoveIcon,
        isVisible: isUserLoggedIn,
        key: 'remove',
        label: t('selections:delete_selection'),
        onClick: deleteOrder,
      },
    ];

    return options.filter(option => option.isVisible).map(option => omit(['isVisible'], option));
  }, [isCsvImportEnabled, isUserLoggedIn, t]);
};
